import React, { memo, useEffect, useMemo, useState } from 'react';
import { Card, Segment } from 'tea-component';
import { useSetState } from 'ahooks';
import _ from 'lodash-es';
import { useIntl } from 'umi';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import { ComponentProps } from '@/pages/intelligence/pages/GlobalView/MarketSize/components/MarketEchartsMap';
import ChartIconType from '@/pages/intelligence/components/ChartsComponents/ChartIconType';
import CommonLoading from '@/pages/intelligence/components/CommonComponents/CommonLoading';
import KolTableList from '../KolTableList';
import { cleanObject } from '@/pages/intelligence/pages/GlobalView/MarketSize/hooks/useQuery';
import Visualis from '@/pages/intelligence/pages/TopCharts/Companies/components/CompaniesCore/components/Visualis';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { useStreamingKolTrend } from '../../../hooks';
import { getBarOrPieData, getLineOrPieData } from '../../../utils';
import { useOptionslistSegment } from '../../../hooks/useFilterOptions';

const StreamingKolEcahrts = ({
  paramsDefault,
  userIds: user_ids,
  loading,
  dateTimes,
  sortTextKeys,
}: Omit<ComponentProps<typeof KolTableList>, 'setParamsDefault'>) => {
  const { formatMessage } = useIntl();
  // echarts图表类型
  const [chartType, setChartType] = useState<ChartType>(ChartType.Bar);
  // 图表参数
  const [kolTrendProps, setKolTrendProps] = useSetState<{
    [key in string]: string;
  }>({
    order_metric: 'hoursWatched',
  });
  const { order_metric, ...restProps } = kolTrendProps;
  const [options, hoverBubbleText, titleText] = useOptionslistSegment(order_metric, sortTextKeys);
  // 图表数据
  const {
    data: cardList,
    loading: echartsLoading,
    error,
    mutate,
  } = useStreamingKolTrend(
    useMemo(
      () => ({
        ...kolTrendProps,
        start_date: dateTimes?.[0],
        end_date: dateTimes?.[1],
        user_ids: user_ids?.userList,
        ...cleanObject(paramsDefault),
      }),
      [kolTrendProps, paramsDefault, user_ids?.userList, dateTimes],
    ),
  );

  useEffect(() => {
    // 当传入的userIds为空时,接口refresh处判断了必须要有user_ids才report,不然接口会报错
    // 所以会导致如果传入的为空，将不会发起请求，此处手动设置[]，也减少了一次请求，缓存时长不变
    // 这个接口将会在背后偷偷的report，将会提前返回缓存内容，
    if (_.isEmpty(user_ids?.userList)) {
      mutate([]);
    }
  }, [mutate, user_ids?.userList]);

  //  tab切换事件
  const handleOnClickTabTypeSelect = (type: string) => {
    setKolTrendProps({ order_metric: type });
  };

  // 切换charts类型事件
  const handleChartType = (value: ChartType) => {
    setChartType(value);
  };
  // 判断数据是否为空
  const isNodata = _.isEmpty(user_ids?.userList) && (_.isEmpty(cardList) as Boolean);

  // 柱状图以及拼图数据
  const barOrPieData = useMemo(() => getBarOrPieData(cardList || []), [cardList]);
  // 折线图数据
  const lineData = useMemo(() => getLineOrPieData(cardList || []), [cardList]);
  // src\pages\intelligence\pages\TopCharts\Companies\components\CompaniesCore\components\Visualis\index.tsx ,整体引入这个组件，与外部业务代码已经解耦，只是把格式转化成对应的，整体不变
  const kolEchartsParams = useMemo(
    () => ({
      topChartsType: 'top_companies_revenue',
      chartType,
      fetchParams: {
        order_metric: 'total_revenue',
        ratio_type: 0,
        granularity: 'daily',
      },
      barOrPieData,
      lineData,
      isError: error,
      isNodata,
      orderTypeInfo: 'desc',
      titleNow: hoverBubbleText,
      imageType: 'user',
      isNew: false,
      hoverCloneType: 'streaming_influencer',
    }),
    [barOrPieData, chartType, error, hoverBubbleText, isNodata, lineData],
  );

  return (
    <>
      <Card className="dbi-mobileGames-card-context">
        <Card.Body
          title={
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>
                {echartsLoading ? null : formatMessage({ id: 'dbi_Primary_Key_metrics' })}
              </span>
              <ArrowdownText isNew={true} sortedData={titleText} order={paramsDefault?.order} />
            </div>
          }
          operation={
            echartsLoading ? null : (
              <ChartIconType chartType={chartType} handleChartType={handleChartType} />
            )
          }
        >
          {loading || echartsLoading ? (
            <CommonLoading />
          ) : (
            <>
              <div className="dbi-kol-details-change">
                <Segment
                  value={order_metric as string}
                  onChange={handleOnClickTabTypeSelect}
                  options={options}
                />
                {/* <ArrowdownText isNew={true} sortedData={options[0]?.text} /> */}
              </div>
              <div className="char-data">
                <Visualis {...kolEchartsParams} />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
export default memo(StreamingKolEcahrts);
